import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import { useSketchStore, createSketchSolid } from '../../state/useSketchStore';
import { facePlane, type SketchFace } from '../../core/sketch/types';
import { splitFaceWithSegments } from '../../core/sketch/faceSplit';
import { planeBasis } from '../../core/sketch/triangulate';
import type { InferenceResult, InferredAxis, InferenceGuideLine } from '../../core/sketch/inference';
import { useSketchToolEnv, loopFromPoints } from './sketchInput';
import { registerSketchTool, type SketchHit } from './sketchToolBus';
import { circleLoopExact, polygonLoopExact, arcLoopExact } from '../../core/sketch/curves';
import type { SketchLoopMeta } from '../../core/sketch/types';
import {
    SketchPolyPreview, SketchLoopPreview, SketchInferenceMarker, SketchAxisLinePreview,
    SketchGuideLinesPreview, inferenceAxisColor,
} from './previews';
import type { ReactNode } from 'react';

/**
 * Sketch draw tools (Line / Rectangle / Circle / Polygon / Arc).
 *
 * Shared contract, Sketch behavior:
 * - Drawing happens on the ACTIVE plane: a hovered face's plane, or the Z=0
 *   ground (plane-following via the sticky plane in useSketchToolEnv).
 * - A closed loop ON a face SPLITS it (faceSplit); in empty space it births
 *   a new planar face-solid (ready for Push/Pull).
 * - Inference (endpoint/midpoint/on-edge/on-axis) resolves every point; the
 *   VCB box commits exact values on Enter.
 * - Escape cancels the stroke; arrow keys lock axes.
 */

export type FaceRef = { solidId: string; faceId: string } | null;

interface StrokeState {
    face: FaceRef;
}

/** Commit a closed loop: split the hit face, or birth a new face-solid.
 *  Shared with the advanced draw tools (Rotated Rectangle / Pie / ...). */
export function commitClosedLoop(
    loop: THREE.Vector3[],
    plane: THREE.Plane,
    face: FaceRef,
    label: string,
    loopMeta?: SketchLoopMeta
): FaceRef {
    if (loop.length < 3) return null;
    const store = useSketchStore.getState();
    const openLoop = loopFromPoints(loop);

    if (face) {
        const solid = store.solids[face.solidId];
        const target = solid?.faces.find((f) => f.id === face.faceId);
        if (solid && target) {
            // Closed loop as one open polyline ending back at the start.
            const result = splitFaceWithSegments(target, [[...openLoop, openLoop[0]]]);
            if (result.ok) {
                // Split result replaces ONLY the target face; every sibling
                // face of the solid must survive (dropping them shears the
                // shell apart — the "draw on a solid breaks it" bug).
                const faces = solid.faces.flatMap((f) => (f.id === target.id ? result.faces : [f]));
                store.updateSolid(face.solidId, { ...solid, faces }, label);
                return { solidId: face.solidId, faceId: result.faces[0].id };
            }
            return face;
        }
    }

    // Empty space: new planar face-solid on the active plane.
    const normal = plane.normal.clone().normalize();
    const origin = new THREE.Vector3(...openLoop[0]);
    const faceData: SketchFace = {
        id: uuidv4(),
        origin: [origin.x, origin.y, origin.z],
        normal: [normal.x, normal.y, normal.z],
        outer: openLoop,
        holes: [],
        loopMeta,
    };
    const solid = createSketchSolid([faceData], 'Face');
    store.addSolid(solid, label);
    return { solidId: solid.id, faceId: faceData.id };
}

/** Commit one OPEN segment onto a face (etch / split-in-progress). */
function commitOpenSegment(
    from: THREE.Vector3,
    to: THREE.Vector3,
    face: FaceRef,
    label: string
): FaceRef {
    if (!face) return null;
    const store = useSketchStore.getState();
    const solid = store.solids[face.solidId];
    const target = solid?.faces.find((f) => f.id === face.faceId);
    if (!solid || !target) return face;
    const result = splitFaceWithSegments(target, [
        [[from.x, from.y, from.z], [to.x, to.y, to.z]],
    ]);
    if (!result.ok) return face;
    // Sibling faces survive the split (see commitClosedLoop).
    const faces = solid.faces.flatMap((f) => (f.id === target.id ? result.faces : [f]));
    store.updateSolid(face.solidId, { ...solid, faces }, label);
    const next = result.faces[0];
    return { solidId: face.solidId, faceId: next.id };
}

/** Shared stroke plumbing for the draw tools. */
function useDrawStroke() {
    const env = useSketchToolEnv();
    const faceRef = useRef<FaceRef>(null);
    // Plane locked at a stroke's first click: hovering faces or loose edges
    // mid-stroke must not move the drawing plane (a stroke hopping planes
    // can never close into a face).
    const strokePlaneRef = useRef<THREE.Plane | null>(null);
    const [, forceRender] = useState(0);
    const rerender = useCallback(() => forceRender((n) => n + 1), []);
    return { env, faceRef, strokePlaneRef, rerender };
}

export const hitFaceRef = (faceRef: React.MutableRefObject<FaceRef>, hit: SketchHit): FaceRef => {
    // A face hit always re-targets the stroke; empty-space hits keep the
    // sticky face (drawing continues on the active plane).
    if (hit.kind === 'face' && hit.solidId && hit.faceId) {
        return { solidId: hit.solidId, faceId: hit.faceId };
    }
    return faceRef.current;
};

/** Face target for the FIRST click of a stroke: a face pick owns the stroke,
 *  any other hit starts a fresh non-sticky stroke (a stale face ref left by
 *  an earlier stroke would etch that old face — or no-op after it was split
 *  — instead of letting new loops form faces). Mid-stroke clicks keep using
 *  hitFaceRef, whose empty-space hits preserve the sticky face. */
export const startFaceRef = (hit: SketchHit): FaceRef =>
    hit.kind === 'face' && hit.solidId && hit.faceId
        ? { solidId: hit.solidId, faceId: hit.faceId }
        : null;

/** Mid-stroke face re-target gated to the locked stroke plane: a face pick
 *  owns the stroke only when it lies on that plane. A loop locked to one
 *  plane can never split a face lying on another plane — adopting such a
 *  target would feed splitFaceWithSegments off-plane points. */
export const strokePlaneFaceRef = (
    faceRef: React.MutableRefObject<FaceRef>,
    hit: SketchHit,
    strokePlane: THREE.Plane
): FaceRef => {
    const next = hitFaceRef(faceRef, hit);
    if (!next || next === faceRef.current) return faceRef.current;
    const solid = useSketchStore.getState().solids[next.solidId];
    const face = solid?.faces.find((f) => f.id === next.faceId);
    if (!face) return faceRef.current;
    const plane = facePlane(face);
    const coplanar =
        Math.abs(plane.normal.dot(strokePlane.normal)) > 0.9999 &&
        Math.abs(strokePlane.distanceToPoint(new THREE.Vector3(...face.origin))) < 1e-6;
    return coplanar ? next : faceRef.current;
};

// ============================== LINE ==============================

export function useSketchLineTool() {
    const { env, faceRef, strokePlaneRef, rerender } = useDrawStroke();
    const [points, setPoints] = useState<THREE.Vector3[]>([]);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                // Mid-stroke the plane is locked: no sticky-plane following,
                // or hovering a face lifts the stroke off its own plane.
                if (points.length > 0 && strokePlaneRef.current) {
                    setCursor(env.infer(hit, points, env.axisLock, strokePlaneRef.current ?? undefined));
                    return;
                }
                setCursor(env.infer(hit, points, env.axisLock));
                env.planeOf(hit);
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                // Stroke start picks the face target fresh and locks the
                // drawing plane; mid-stroke hits keep both (no plane hopping).
                if (points.length === 0) {
                    faceRef.current = startFaceRef(hit);
                    strokePlaneRef.current = env.planeOf(hit).clone();
                }
                const strokePlane = strokePlaneRef.current ?? env.planeOf(hit);
                const res = env.infer(hit, points, env.axisLock, strokePlane);
                let point = res.point.clone();
                const prev = points[points.length - 1];
                // VCB exact length overrides the endpoint along the preview ray.
                const vcb = env.consumeVcb();
                if (vcb !== null && prev) {
                    const dir = point.clone().sub(prev);
                    if (dir.lengthSq() > 1e-12) point = prev.clone().addScaledVector(dir.normalize(), vcb);
                }
                // Loop closure: click back on the stroke's first point. The
                // pick tolerance is screen-sized (not a hard epsilon), so the
                // loop closes even with object snaps off or the click just
                // outside the snap aperture — a completed loop ALWAYS forms
                // a face. On a face the closing loop splits it; in empty
                // space the closing segment lands in the loose-edge graph
                // and detectClosedRegions coalesces it (Sketch's auto-face).
                const first = points[0];
                if (first && points.length >= 3 && point.distanceTo(first) < env.worldToleranceAt(first)) {
                    if (faceRef.current) {
                        commitClosedLoop([...points], strokePlane, faceRef.current, 'Draw Face');
                    } else if (prev) {
                        const [ta, tb] = loopFromPoints([prev, first]);
                        useSketchStore.getState().addLooseSegment(ta, tb, strokePlane, 'Draw Line');
                    }
                    faceRef.current = null;
                    strokePlaneRef.current = null;
                    setPoints([]);
                    setCursor(null);
                    rerender();
                    return;
                }
                if (prev) {
                    if (faceRef.current) {
                        faceRef.current = commitOpenSegment(prev, point, faceRef.current, 'Draw Line');
                    } else {
                        // Open stroke in empty space: the segment persists as a
                        // loose edge; a later segment may close it into a face.
                        const [ta, tb] = loopFromPoints([prev, point]);
                        useSketchStore.getState().addLooseSegment(ta, tb, strokePlane, 'Draw Line');
                    }
                }
                setPoints([...points, point]);
                rerender();
            },
        });
        return () => registerSketchTool(null);
    }, [env, faceRef, rerender, points]);

    // Escape cancels the stroke (registered outside the bus: global).
    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            faceRef.current = null;
            strokePlaneRef.current = null;
            setPoints([]);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { points, cursor };
}

// ============================== RECTANGLE ==============================

export function useSketchRectTool() {
    const { env, faceRef, strokePlaneRef, rerender } = useDrawStroke();
    const [corner, setCorner] = useState<THREE.Vector3 | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);
    // Live drawing-plane normal for the preview (matches the committed loop).
    const [planeNormal, setPlaneNormal] = useState(() => new THREE.Vector3(0, 0, 1));

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                // Mid-stroke the plane is locked (see the Line tool): hovering
                // faces cannot hop the rectangle onto another plane, so the
                // two corners always share one plane and the committed loop
                // stays flat (no warped / bowed quads).
                if (corner && strokePlaneRef.current) {
                    const locked = strokePlaneRef.current;
                    setCursor(env.infer(hit, [corner], env.axisLock, locked));
                    setPlaneNormal(locked.normal.clone().normalize());
                    return;
                }
                setCursor(env.infer(hit, corner ? [corner] : [], env.axisLock));
                setPlaneNormal(env.planeOf(hit).normal.clone().normalize());
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                if (!corner) {
                    // Stroke start picks the face target fresh and locks the
                    // drawing plane; mid-stroke hits keep both (no plane
                    // hopping — the Line tool's contract).
                    faceRef.current = startFaceRef(hit);
                    strokePlaneRef.current = env.planeOf(hit).clone();
                } else {
                    faceRef.current = strokePlaneFaceRef(
                        faceRef, hit, strokePlaneRef.current ?? env.planeOf(hit),
                    );
                }
                const strokePlane = strokePlaneRef.current ?? env.planeOf(hit);
                const res = env.infer(hit, corner ? [corner] : [], env.axisLock, strokePlane);
                setPlaneNormal(strokePlane.normal.clone().normalize());
                if (!corner) {
                    setCorner(res.point.clone());
                    rerender();
                    return;
                }
                let second = res.point.clone();
                if (env.axisLock === 'x') second.setY(corner.y);
                if (env.axisLock === 'y') second.setX(corner.x);
                const vcb = env.consumeVcb();
                const plane = strokePlane;
                const { u, v } = planeBasis(plane.normal);
                if (vcb !== null) {
                    // "width" squares out on the plane basis (v1: single value).
                    second = corner.clone().addScaledVector(u, vcb).addScaledVector(v, vcb);
                }
                const du = second.clone().sub(corner);
                const a = corner.clone();
                const b = corner.clone().addScaledVector(u, du.dot(u));
                const c = second.clone();
                const d = corner.clone().addScaledVector(v, du.dot(v));
                commitClosedLoop([a, b, c, d], plane, faceRef.current, 'Draw Rectangle');
                strokePlaneRef.current = null;
                setCorner(null);
                setCursor(null);
                rerender();
            },
        });
        return () => registerSketchTool(null);
    }, [env, faceRef, rerender, corner]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setCorner(null);
            setCursor(null);
            strokePlaneRef.current = null;
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { corner, cursor, planeNormal };
}

// ============================== CIRCLE / POLYGON ==============================

export function useSketchCircleTool(sides = 0) {
    const { env, faceRef, rerender } = useDrawStroke();
    const [center, setCenter] = useState<THREE.Vector3 | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);
    // Live drawing-plane normal for the preview (matches the committed loop).
    const [planeNormal, setPlaneNormal] = useState(() => new THREE.Vector3(0, 0, 1));
    // Segment/side count: the tool default, overridable by typing an integer
    // into the VCB BEFORE the first click (SketchUp's "10 + Enter" flow).
    const [sideCount, setSideCount] = useState(sides);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, center ? [center] : [], null));
                setPlaneNormal(env.planeOf(hit).normal.clone().normalize());
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                faceRef.current = center ? hitFaceRef(faceRef, hit) : startFaceRef(hit);
                const res = env.infer(hit, center ? [center] : [], null);
                const plane = env.planeOf(hit);
                setPlaneNormal(plane.normal.clone().normalize());
                if (!center) {
                    // Pre-click integer in the VCB sets the segment count.
                    const vcb = env.consumeVcb();
                    if (vcb !== null && Number.isInteger(vcb) && vcb >= 3) {
                        setSideCount(Math.floor(vcb));
                        rerender();
                        return;
                    }
                    setCenter(res.point.clone());
                    rerender();
                    return;
                }
                let radius = center.distanceTo(res.point);
                const vcb = env.consumeVcb();
                if (vcb !== null) radius = Math.abs(vcb);
                const exact = radius > 1e-6
                    ? (sideCount > 0
                        ? polygonLoopExact(center, radius, plane.normal, sideCount)
                        : circleLoopExact(center, radius, plane.normal, 24))
                    : null;
                if (exact) {
                    commitClosedLoop(exact.loop, plane, faceRef.current, sideCount > 0 ? 'Draw Polygon' : 'Draw Circle', exact.meta);
                }
                setCenter(null);
                setCursor(null);
                rerender();
            },
        });
        return () => registerSketchTool(null);
    }, [env, faceRef, rerender, center, sideCount]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setCenter(null);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { center, cursor, planeNormal, sideCount };
}

// ============================== ARC ==============================

export function useSketchArcTool() {
    const { env, faceRef, rerender } = useDrawStroke();
    const [start, setStart] = useState<THREE.Vector3 | null>(null);
    const [end, setEnd] = useState<THREE.Vector3 | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, start ? [start] : [], null));
                env.planeOf(hit);
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                faceRef.current = start ? hitFaceRef(faceRef, hit) : startFaceRef(hit);
                const res = env.infer(hit, start ? [start] : [], null);
                if (!start) {
                    setStart(res.point.clone());
                    rerender();
                    return;
                }
                if (!end) {
                    setEnd(res.point.clone());
                    rerender();
                    return;
                }
                // Third click: bulge point → closed arc face (arc + chord).
                const plane = env.planeOf(hit);
                const exact = arcLoopExact(start, res.point, end, 16);
                if (exact) {
                    commitClosedLoop(exact.loop, plane, faceRef.current, 'Draw Arc', exact.meta);
                }
                setStart(null);
                setEnd(null);
                setCursor(null);
                rerender();
            },
        });
        return () => registerSketchTool(null);
    }, [env, faceRef, rerender, start, end]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setStart(null);
            setEnd(null);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { start, end, cursor };
}

// ============================== helpers ==============================

// Preview renderer shared by the draw tools (mounted by SketchToolScope).
export function SketchDrawPreviews({
    rubber,
    loop,
    inference,
    axisLine,
    axis,
}: {
    rubber?: { points: THREE.Vector3[] } | null;
    loop?: THREE.Vector3[] | null;
    inference?: {
        point: THREE.Vector3;
        kind: InferenceResult['kind'];
        guideLines?: InferenceGuideLine[];
    } | null;
    axisLine?: { a: THREE.Vector3; b: THREE.Vector3 } | null;
    axis?: InferredAxis;
}): ReactNode {
    // Sketch axis language: while the cursor rides an axis, the rubber band
    // takes that axis's color (red X / green Y / blue Z, magenta parallel).
    const rubberColor = inference?.kind === 'onAxis' ? inferenceAxisColor(axis) : undefined;
    return (
        <group>
            <DrawPreviewLines points={rubber?.points ?? null} color={rubberColor} />
            <DrawPreviewLoop loop={loop ?? null} />
            {inference && <InferenceDot point={inference.point} kind={inference.kind} axis={axis} />}
            {inference?.guideLines && <SketchGuideLinesPreview lines={inference.guideLines} />}
            {axisLine && <AxisPreview line={axisLine} axis={axis} />}
        </group>
    );
}

// Small indirections so the previews module stays the only JSX/renderer owner.
function DrawPreviewLines({ points, color }: { points: THREE.Vector3[] | null; color?: string }) {
    if (!points || points.length < 2) return null;
    return <SketchPolyPreview vertices={points.slice(0, -1)} cursor={points[points.length - 1]} color={color} />;
}

function DrawPreviewLoop({ loop }: { loop: THREE.Vector3[] | null }) {
    if (!loop || loop.length < 3) return null;
    return <SketchLoopPreview loop={loop} />;
}

function InferenceDot({ point, kind, axis }: {
    point: THREE.Vector3;
    kind: InferenceResult['kind'];
    axis?: InferredAxis;
}) {
    return <SketchInferenceMarker point={point} kind={kind} axis={axis} />;
}

function AxisPreview({ line, axis }: {
    line: { a: THREE.Vector3; b: THREE.Vector3 };
    axis?: InferredAxis;
}) {
    return <SketchAxisLinePreview line={line} axis={axis} />;
}
